import { FC, useEffect, useRef } from 'react';
import { Avatar, Checkbox, Flex, Radio, Tag, Typography } from 'antd';
import styled from 'styled-components';

import { clientConfig } from '@/config/clientConfig';
import { AccountStatus } from '@/shared/generated/graphql';

import { InviteButton } from './InviteButton';
import { SearchMatchText } from './SearchMatchText';
import { NextOwnerUser, SetInviteUser } from './type';
const { Text } = Typography;

type CategoryItemProps = {
  user: NextOwnerUser;
  isShowStatus?: boolean;
  invitedUsers: Array<string>;
  newUserEmail: string | null;
  searchValue: string;
  mode: 'multiple' | 'tags' | undefined;
  checked: boolean | undefined;
  handleSelect: (value?: string) => void;
  setModalInviteUserState: SetInviteUser;
};

export const CategoryItem: FC<CategoryItemProps> = ({
  user,
  isShowStatus,
  invitedUsers,
  newUserEmail,
  searchValue,
  mode,
  checked,
  handleSelect,
  setModalInviteUserState,
}) => {
  const newUserRef = useRef<HTMLDivElement>(null);
  const intlLocale = clientConfig.intlLocale;

  // Scroll to created beneficiary
  useEffect(() => {
    if (newUserRef.current) {
      newUserRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      handleSelect(user.value);
    }
  }, [newUserRef.current]);

  // Set right status
  const getInviteText = (nextOwnerUser: NextOwnerUser) => {
    let text = '';
    let date = '';

    if (nextOwnerUser.status === AccountStatus.Active && nextOwnerUser.createdAt) {
      text = 'Registered';
      date = nextOwnerUser.createdAt && nextOwnerUser.createdAt.toLocaleDateString(intlLocale);
    }

    if (nextOwnerUser.status === AccountStatus.Inactive && nextOwnerUser.invitationLastSentTime) {
      text = 'Invited';

      date =
        nextOwnerUser.invitationLastSentTime &&
        new Date(nextOwnerUser.invitationLastSentTime).toLocaleDateString(intlLocale);
    }

    if (
      nextOwnerUser.status === AccountStatus.Inactive &&
      !nextOwnerUser.invitationLastSentTime &&
      nextOwnerUser.createdAt
    ) {
      text = 'Created';
      date = nextOwnerUser.createdAt && nextOwnerUser.createdAt.toLocaleDateString(intlLocale);
    }

    return `${text} ${date}`;
  };

  return (
    <Item
      onClick={() => {
        if (user.value) {
          handleSelect(user.value);
        }
      }}
    >
      <ItemContent>
        <Flex>
          <StyledAvatar
            icon={<UserAvatarSvg />}
            shape="circle"
            size={36}
            style={{ background: '#c9ced7' }}
          />

          <ContentWrapper>
            <BeneficiaryTitle>
              <SearchMatchText text={String(user.label)} matchString={searchValue} />

              {isShowStatus && user.status === AccountStatus.Active && (
                <StyledTag color="green">Registered</StyledTag>
              )}

              {isShowStatus &&
                user.status === AccountStatus.Inactive &&
                user.invitationLastSentTime && <StyledTag color="orange">Not registered</StyledTag>}

              {isShowStatus &&
                user.status === AccountStatus.Inactive &&
                !user.invitationLastSentTime && <StyledTag color="orange">Not Invited</StyledTag>}
            </BeneficiaryTitle>

            <Flex justify="space-between" align="center">
              <Flex vertical>
                <TitleWrapper>
                  <Status>{getInviteText(user)}</Status>

                  {user.status === AccountStatus.Inactive && (
                    <InviteButton
                      code={user.invitationCode}
                      email={user.email}
                      id={user.centralDbProfileId || user.id}
                      invitedUsers={invitedUsers}
                      isFirstInvite={!user.invitationLastSentTime}
                      setModalInviteUserState={setModalInviteUserState}
                    />
                  )}
                </TitleWrapper>

                <SearchMatchText
                  matchString={searchValue}
                  text={String(user.email)}
                  textSize="10"
                />
              </Flex>
            </Flex>
          </ContentWrapper>
        </Flex>
      </ItemContent>

      <ItemAction>
        <Flex gap={8}>
          {newUserEmail && newUserEmail === user.email && <NewTag ref={newUserRef}>New</NewTag>}

          {mode === 'multiple' ? (
            <Checkbox value={user.value} key={user.email} checked={checked} />
          ) : (
            <Radio value={user.value} key={user.email} />
          )}
        </Flex>
      </ItemAction>
    </Item>
  );
};

const Item = styled.div`
  position: relative;
  display: flex;
  padding: 8px 16px;
  gap: 12px;
  background: #ffffff;
  flex-direction: row-reverse;

  @media (max-width: 991px) {
    flex-direction: row;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    border-bottom: 0.3px solid #d0d4dc;
  }
`;

const ItemContent = styled.div`
  flex: 1;
`;

const StyledAvatar = styled(Avatar)`
  &&& {
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: center;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ContentWrapper = styled.div`
  display: grid;
  flex: 1;
  padding: 0 12px;
`;

const ItemAction = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const BeneficiaryTitle = styled(Flex)`
  justify-content: flex-start;
  gap: 12px;

  @media (max-width: 991px) {
    justify-content: space-between;
  }
`;

const StyledTag = styled(Tag)`
  &&& {
    border: none;
    margin-right: 0;
  }
`;

const NewTag = styled(Text)`
  &&& {
    font-family: 'Manrope';
    color: #019f3c;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }
`;

const Status = styled(Text)`
  &&& {
    font-family: 'Manrope';
    font-size: 10px;
  }
`;

const UserAvatarSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 1.875C8.39303 1.875 6.82214 2.35152 5.486 3.24431C4.14985 4.1371 3.10844 5.40605 2.49348 6.8907C1.87852 8.37535 1.71762 10.009 2.03112 11.5851C2.34463 13.1612 3.11846 14.6089 4.25476 15.7452C5.39106 16.8815 6.8388 17.6554 8.4149 17.9689C9.99099 18.2824 11.6247 18.1215 13.1093 17.5065C14.594 16.8916 15.8629 15.8502 16.7557 14.514C17.6485 13.1779 18.125 11.607 18.125 10C18.1227 7.84581 17.266 5.78051 15.7427 4.25727C14.2195 2.73403 12.1542 1.87727 10 1.875ZM5.7875 15.4297C6.23964 14.7226 6.86251 14.1406 7.59869 13.7375C8.33488 13.3345 9.16069 13.1232 10 13.1232C10.8393 13.1232 11.6651 13.3345 12.4013 13.7375C13.1375 14.1406 13.7604 14.7226 14.2125 15.4297C13.0081 16.3664 11.5258 16.8749 10 16.8749C8.4742 16.8749 6.99193 16.3664 5.7875 15.4297ZM7.5 9.375C7.5 8.88055 7.64663 8.3972 7.92133 7.98607C8.19603 7.57495 8.58648 7.25452 9.04329 7.0653C9.50011 6.87608 10.0028 6.82657 10.4877 6.92304C10.9727 7.0195 11.4181 7.2576 11.7678 7.60723C12.1174 7.95686 12.3555 8.40232 12.452 8.88727C12.5484 9.37223 12.4989 9.87489 12.3097 10.3317C12.1205 10.7885 11.8001 11.179 11.3889 11.4537C10.9778 11.7284 10.4945 11.875 10 11.875C9.33696 11.875 8.70108 11.6116 8.23224 11.1428C7.7634 10.6739 7.5 10.038 7.5 9.375ZM15.1375 14.5633C14.4404 13.5532 13.4603 12.7717 12.3203 12.3172C12.9327 11.8349 13.3795 11.1737 13.5987 10.4257C13.8179 9.67766 13.7985 8.87992 13.5433 8.1434C13.2881 7.40687 12.8097 6.76819 12.1746 6.31616C11.5396 5.86414 10.7795 5.62123 10 5.62123C9.22052 5.62123 8.4604 5.86414 7.82536 6.31616C7.19033 6.76819 6.71193 7.40687 6.45671 8.1434C6.20149 8.87992 6.18212 9.67766 6.40131 10.4257C6.62049 11.1737 7.06734 11.8349 7.67969 12.3172C6.5397 12.7717 5.55956 13.5532 4.8625 14.5633C3.9817 13.5728 3.406 12.3488 3.20473 11.0387C3.00346 9.72856 3.1852 8.3882 3.72806 7.17898C4.27093 5.96977 5.15178 4.94326 6.26454 4.22308C7.3773 3.50289 8.67452 3.11972 10 3.11972C11.3255 3.11972 12.6227 3.50289 13.7355 4.22308C14.8482 4.94326 15.7291 5.96977 16.2719 7.17898C16.8148 8.3882 16.9966 9.72856 16.7953 11.0387C16.594 12.3488 16.0183 13.5728 15.1375 14.5633Z"
      fill="#445371"
    />
  </svg>
);
